import { useState, useEffect, useMemo, useRef, useCallback, useTransition } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DatePicker } from '@/components/ui/date-picker';
import { toDateParam } from '@/lib/date';
import {
  usePositions,
  useDepartments,
  useSeniorityLevels,
  useEmploymentTypes,
} from '@/hooks/use-organizations';
import { useRoles } from '@/hooks/use-roles-permissions';
import { useIsSuperAdmin, usePermission } from '@/lib/permissions';
import {
  assignableRoles,
  isSuperAdminOnlyRole,
  pickDefaultRole,
} from '@/features/people/lib/assignable-roles';
import type { CreateEmployeeInput } from '@/lib/employees';
import { getCompanySettings, type CompanySettings } from '@/lib/settings';
import type { EmployeeListItem } from '@/types/employee';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ManagerCombobox } from './manager-combobox';

export interface EmployeeFormDefaults {
  firstName?: string;
  lastName?: string;
  workEmail?: string;
  /** When true, the work email field is disabled — useful when creating an employee for an existing user account. */
  workEmailLocked?: boolean;
  personalEmail?: string;
  roleId?: string;
  /** `null` = the source (e.g. vacancy) resolved but carries none — reset a prior
   * prefill to "None"; `undefined` = unknown / still loading, so leave the field be. */
  departmentId?: string | null;
  seniorityLevelId?: string | null;
  startDate?: Date;
}

// Turn a name into the local part of an address: characters an address cannot carry are
// dropped, inner whitespace becomes a dot, dot runs collapse and edge dots are stripped.
// A multi-word part ("Van der Berg", a 3-word full name) or one carrying an abbreviation
// ("Smith Jr.", "Doe, John") therefore no longer leaves a space or a stray dot behind.
function toEmailLocalPart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[,;:<>()[\]\\"]+/g, '')
    .replace(/\s+/g, '.')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '');
}

// The host follows a stricter grammar than the local part — letters, digits and hyphens only,
// with every dot-separated label starting and ending alphanumeric — so the company name gets
// its own pass. Hyphens are trimmed per label rather than at the ends of the whole host, which
// is what keeps a name like "Hygge - Software" usable: it becomes "hygge.software" instead of
// "hygge.-.software", which the browser refuses. Also "Acme & Co" → "acme.co", "Smith_Co Ltd"
// → "smithco.ltd", "Acme Corp." → "acme.corp".
function toEmailHost(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s.-]+/g, '')
    .replace(/\s+/g, '.')
    .split('.')
    .map((label) => label.replace(/^-+|-+$/g, ''))
    .filter(Boolean)
    .join('.');
}

// Only prefill an address every layer accepts. The field is `type="email"`, so the browser
// refuses a bad value before `handleSubmit` ever runs — no request, no inline error, only its
// own tooltip — the validator below additionally wants a dot in the host, and the API is
// stricter still (length limits, an alphabetic TLD). This pattern is a deliberately
// conservative subset of all three. Anything outside it — a name that isn't plain ASCII
// (accents included), a company name that normalizes away or carries no dot — leaves the field
// empty for the user to type, which beats seeding a value that blocks the form with nothing on
// screen to explain why. (HW-764)
const PREFILLABLE_EMAIL =
  /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

interface EmployeeFormProps {
  defaults?: EmployeeFormDefaults;
  onSubmit: (input: CreateEmployeeInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  serverError?: string | null;
  submitLabel?: string;
  showPersonalEmail?: boolean;
  /** HW-676: when true, at least one manager must be selected before submit
   * (enforced only in the hire flow — the general Add Employee flow leaves it optional). */
  requireManager?: boolean;
  /** Slot rendered between the manager picker and the footer — caller can inject
   * flow-specific controls (e.g. the hire flow's "Reject other applications"). */
  extraSection?: React.ReactNode;
}

export function EmployeeForm({
  defaults,
  onSubmit,
  onCancel,
  isSubmitting,
  serverError,
  submitLabel = 'Create',
  showPersonalEmail = false,
  requireManager = false,
  extraSection,
}: EmployeeFormProps) {
  // Uncontrolled inputs (smooth typing — no re-renders on every keystroke)
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const workEmailRef = useRef<HTMLInputElement>(null);
  const personalEmailRef = useRef<HTMLInputElement>(null);
  const emailTouchedRef = useRef(false);

  const { data: companySettings } = useQuery<CompanySettings>({
    queryKey: ['companySettings'],
    queryFn: getCompanySettings,
  });

  const autoFillEmail = useCallback(() => {
    if (emailTouchedRef.current || !workEmailRef.current) return;
    const firstName = toEmailLocalPart(firstNameRef.current?.value ?? '');
    const lastName = toEmailLocalPart(lastNameRef.current?.value ?? '');
    const localPart = firstName && lastName ? `${firstName}.${lastName}` : firstName || lastName;
    const email = `${localPart}@${toEmailHost(companySettings?.name ?? '')}`;
    // Clear rather than keep whatever we wrote last time: that address was derived from a name
    // the user has since changed, and unlike the old broken values it is perfectly submittable
    // — so leaving it behind creates the employee (and their login) under the wrong address.
    workEmailRef.current.value =
      email.length <= 254 && PREFILLABLE_EMAIL.test(email) ? email : '';
  }, [companySettings?.name]);

  const [positionId, setPositionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [seniorityLevelId, setSeniorityLevelId] = useState('');
  const [selectedManagers, setSelectedManagers] = useState<EmployeeListItem[]>([]);
  const [employmentTypeId, setEmploymentTypeId] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(defaults?.startDate ?? null);
  const [probationEndDate, setProbationEndDate] = useState<Date | null>(null);
  const [roleId, setRoleId] = useState<string>('');
  const [roleInitialized, setRoleInitialized] = useState(false);

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    workEmail?: string;
    personalEmail?: string;
    employmentType?: string;
    startDate?: string;
    manager?: string;
  }>({});

  const { data: positions = [], isLoading: positionsLoading } = usePositions();
  const { data: departments = [], isLoading: departmentsLoading } = useDepartments();
  const { data: seniorityLevels = [], isLoading: seniorityLevelsLoading } = useSeniorityLevels();
  const { data: employmentTypes = [], isLoading: employmentTypesLoading } = useEmploymentTypes();
  const isSuperAdmin = useIsSuperAdmin();
  // A system role creates a user account, which takes Create employees on the
  // server for Add Employee and for Hire alike; without it the form offers no
  // role, sends none and does not load the role list, so a hire still goes
  // through. [HW-1061]
  const canAssignRole = usePermission('employees.profile.create');
  const { data: allRoles = [] } = useRoles(canAssignRole);
  // super_admin is offered to a super_admin only; the server refuses it from
  // anyone else. [HW-1061]
  const roles = useMemo(
    () => (canAssignRole ? assignableRoles(allRoles, isSuperAdmin) : []),
    [allRoles, isSuperAdmin, canAssignRole]
  );
  // A role holding a permission only super_admin may grant (the matrix's write
  // codes, Login as employee, the placeholder) is given by a super_admin only;
  // it stays listed, disabled, so the reason is visible instead of a 403 on
  // save. [HW-1062]
  const lockedRoleIds = useMemo(
    () => new Set(isSuperAdmin ? [] : roles.filter(isSuperAdminOnlyRole).map((r) => r.id)),
    [roles, isSuperAdmin]
  );

  useEffect(() => {
    if (roles.length > 0 && !roleInitialized) {
      if (defaults?.roleId && roles.some((r) => r.id === defaults.roleId)) {
        setRoleId(defaults.roleId);
      } else {
        // A locked `employee` would be pre-selected yet refused on save; the
        // field then starts on "No account". [HW-1062]
        const defaultRole = pickDefaultRole(roles, lockedRoleIds);
        if (defaultRole) setRoleId(defaultRole.id);
      }
      setRoleInitialized(true);
    }
  }, [roles, roleInitialized, defaults?.roleId, lockedRoleIds]);

  // Sync Department / Seniority to the vacancy defaults. Both the vacancy and the
  // reference lists resolve asynchronously (and on the profile / candidates-list entry
  // points they arrive after the form mounts), so this runs as an effect rather than
  // useState init. `undefined` means "still loading" — leave the field alone; a string
  // id or explicit `null` means the vacancy resolved, so sync the field to it: a valid
  // id fills it, while `null` or an archived/unknown id resets it to "None" (never a
  // phantom Select option). It re-applies when the selected vacancy changes
  // (multi-application hire) but never clobbers a field the user has since picked or
  // cleared — prefilledRef holds the last value we wrote, and we only overwrite while
  // the field still equals it. Each field is gated on its own reference list. (HW-675)
  const prefilledRef = useRef<{ departmentId?: string; seniorityLevelId?: string }>({});
  useEffect(() => {
    const wantDepartment = defaults?.departmentId;
    if (!departmentsLoading && wantDepartment !== undefined) {
      const target =
        wantDepartment && departments.some((d) => d.id === wantDepartment) ? wantDepartment : '';
      if (departmentId === (prefilledRef.current.departmentId ?? '') && departmentId !== target) {
        setDepartmentId(target);
        prefilledRef.current.departmentId = target || undefined;
      }
    }

    const wantSeniority = defaults?.seniorityLevelId;
    if (!seniorityLevelsLoading && wantSeniority !== undefined) {
      const target =
        wantSeniority && seniorityLevels.some((s) => s.id === wantSeniority) ? wantSeniority : '';
      if (
        seniorityLevelId === (prefilledRef.current.seniorityLevelId ?? '') &&
        seniorityLevelId !== target
      ) {
        setSeniorityLevelId(target);
        prefilledRef.current.seniorityLevelId = target || undefined;
      }
    }
  }, [
    defaults?.departmentId,
    defaults?.seniorityLevelId,
    departments,
    seniorityLevels,
    departmentsLoading,
    seniorityLevelsLoading,
    departmentId,
    seniorityLevelId,
  ]);

  // Mark email as "touched" when a default is provided so autofill doesn't clobber it.
  useEffect(() => {
    if (defaults?.workEmail) {
      emailTouchedRef.current = true;
    }
  }, [defaults?.workEmail]);

  // When opened with prefilled name but no email (hire flow), seed the email
  // field once on mount and again after companySettings.name resolves — without
  // this users see an empty email until they tab into the first/last fields.
  useEffect(() => {
    if (defaults?.workEmail) return;
    if (!defaults?.firstName && !defaults?.lastName) return;
    autoFillEmail();
  }, [defaults?.firstName, defaults?.lastName, defaults?.workEmail, autoFillEmail]);

  const isReferenceDataLoading =
    positionsLoading || departmentsLoading || seniorityLevelsLoading || employmentTypesLoading;

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const [, startTransition] = useTransition();

  const addManager = (manager: EmployeeListItem) => {
    if (selectedManagers.some((m) => m.id === manager.id)) return;
    startTransition(() => setSelectedManagers((prev) => [...prev, manager]));
    if (errors.manager) setErrors((prev) => ({ ...prev, manager: undefined }));
  };

  const removeManager = (managerId: string) => {
    startTransition(() => setSelectedManagers((prev) => prev.filter((m) => m.id !== managerId)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const firstName = firstNameRef.current?.value.trim() ?? '';
    const lastName = lastNameRef.current?.value.trim() ?? '';
    const workEmail = workEmailRef.current?.value.trim() ?? '';
    const personalEmail = personalEmailRef.current?.value.trim() ?? '';

    const newErrors: typeof errors = {};
    if (!firstName) newErrors.firstName = 'Required';
    if (!lastName) newErrors.lastName = 'Required';
    if (!workEmail) newErrors.workEmail = 'Required';
    else if (!validateEmail(workEmail)) newErrors.workEmail = 'Invalid email';
    if (personalEmail && !validateEmail(personalEmail)) newErrors.personalEmail = 'Invalid email';
    if (!employmentTypeId) newErrors.employmentType = 'Required';
    if (!startDate) newErrors.startDate = 'Required';
    if (requireManager && selectedManagers.length === 0) newErrors.manager = 'Required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const input: CreateEmployeeInput = {
      firstName,
      lastName,
      workEmail,
      employmentTypeId,
      startDate: toDateParam(startDate)!,
    };
    if (personalEmail) input.personalEmail = personalEmail;
    if (positionId) input.positionId = positionId;
    if (departmentId) input.departmentId = departmentId;
    if (seniorityLevelId) input.seniorityLevelId = seniorityLevelId;
    if (selectedManagers.length > 0) input.managerIds = selectedManagers.map((m) => m.id);
    if (canAssignRole && roleId) input.roleId = roleId;
    if (probationEndDate) input.probationEndDate = toDateParam(probationEndDate)!;

    onSubmit(input);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {serverError && (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive flex items-start gap-2 leading-snug"
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="flex-1">{serverError}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="firstName" className="text-xs font-medium text-muted-foreground">
            First name <span className="text-destructive">*</span>
          </Label>
          <Input
            ref={firstNameRef}
            id="firstName"
            defaultValue={defaults?.firstName ?? ''}
            disabled={isSubmitting}
            placeholder="John"
            aria-invalid={!!errors.firstName}
            aria-describedby={errors.firstName ? 'firstName-error' : undefined}
            onInput={autoFillEmail}
            onBlur={() => {
              autoFillEmail();
              if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
            }}
            className={cn(
              'h-9',
              errors.firstName && 'border-destructive focus-visible:ring-destructive',
            )}
          />
          {errors.firstName && (
            <p id="firstName-error" className="text-xs text-destructive" role="alert">
              {errors.firstName}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="lastName" className="text-xs font-medium text-muted-foreground">
            Last name <span className="text-destructive">*</span>
          </Label>
          <Input
            ref={lastNameRef}
            id="lastName"
            defaultValue={defaults?.lastName ?? ''}
            disabled={isSubmitting}
            placeholder="Doe"
            aria-invalid={!!errors.lastName}
            aria-describedby={errors.lastName ? 'lastName-error' : undefined}
            onInput={autoFillEmail}
            onBlur={() => {
              autoFillEmail();
              if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
            }}
            className={cn(
              'h-9',
              errors.lastName && 'border-destructive focus-visible:ring-destructive',
            )}
          />
          {errors.lastName && (
            <p id="lastName-error" className="text-xs text-destructive" role="alert">
              {errors.lastName}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="workEmail" className="text-xs font-medium text-muted-foreground">
          Work email <span className="text-destructive">*</span>
        </Label>
        <Input
          ref={workEmailRef}
          id="workEmail"
          type="email"
          defaultValue={defaults?.workEmail ?? ''}
          disabled={isSubmitting || defaults?.workEmailLocked}
          readOnly={defaults?.workEmailLocked}
          placeholder="john.doe@company.com"
          aria-invalid={!!errors.workEmail}
          aria-describedby={errors.workEmail ? 'workEmail-error' : undefined}
          onInput={() => {
            emailTouchedRef.current = true;
          }}
          onBlur={() =>
            errors.workEmail && setErrors((prev) => ({ ...prev, workEmail: undefined }))
          }
          className={cn(
            'h-9',
            errors.workEmail && 'border-destructive focus-visible:ring-destructive',
          )}
        />
        {errors.workEmail && (
          <p id="workEmail-error" className="text-xs text-destructive" role="alert">
            {errors.workEmail}
          </p>
        )}
      </div>

      {showPersonalEmail && (
        <div className="space-y-1.5">
          <Label htmlFor="personalEmail" className="text-xs font-medium text-muted-foreground">
            Personal email
          </Label>
          <Input
            ref={personalEmailRef}
            id="personalEmail"
            type="email"
            defaultValue={defaults?.personalEmail ?? ''}
            disabled={isSubmitting}
            placeholder="name@gmail.com"
            aria-invalid={!!errors.personalEmail}
            aria-describedby={errors.personalEmail ? 'personalEmail-error' : undefined}
            onBlur={() =>
              errors.personalEmail && setErrors((prev) => ({ ...prev, personalEmail: undefined }))
            }
            className={cn(
              'h-9',
              errors.personalEmail && 'border-destructive focus-visible:ring-destructive',
            )}
          />
          {errors.personalEmail && (
            <p id="personalEmail-error" className="text-xs text-destructive" role="alert">
              {errors.personalEmail}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Position</Label>
          <SearchableSelect
            value={positionId}
            onChange={setPositionId}
            options={positions.map((p) => ({ id: p.id, name: p.title }))}
            placeholder="Select..."
            emptyText="No results found"
            disabled={isReferenceDataLoading || isSubmitting}
            modal
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="department" className="text-xs font-medium text-muted-foreground">
            Department
          </Label>
          <Select
            value={departmentId || '__none__'}
            onValueChange={(value) => setDepartmentId(value === '__none__' ? '' : value)}
            disabled={isReferenceDataLoading || isSubmitting}
          >
            <SelectTrigger id="department" className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {departments.map((department) => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="seniorityLevel" className="text-xs font-medium text-muted-foreground">
            Seniority
          </Label>
          <Select
            value={seniorityLevelId || '__none__'}
            onValueChange={(value) => setSeniorityLevelId(value === '__none__' ? '' : value)}
            disabled={isReferenceDataLoading || isSubmitting}
          >
            <SelectTrigger id="seniorityLevel" className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {seniorityLevels.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="employmentType" className="text-xs font-medium text-muted-foreground">
            Employment type <span className="text-destructive">*</span>
          </Label>
          <Select
            value={employmentTypeId || '__none__'}
            onValueChange={(v) => {
              setEmploymentTypeId(v === '__none__' ? '' : v);
              if (errors.employmentType)
                setErrors((prev) => ({ ...prev, employmentType: undefined }));
            }}
            disabled={isReferenceDataLoading || isSubmitting}
          >
            <SelectTrigger
              id="employmentType"
              className="h-9"
              aria-invalid={!!errors.employmentType}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select...</SelectItem>
              {employmentTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.employmentType && (
            <p className="text-xs text-destructive" role="alert">
              {errors.employmentType}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="startDate" className="text-xs font-medium text-muted-foreground">
            Start date <span className="text-destructive">*</span>
          </Label>
          <DatePicker
            id="startDate"
            value={startDate}
            onChange={(date) => {
              setStartDate(date);
              if (errors.startDate) setErrors((prev) => ({ ...prev, startDate: undefined }));
            }}
            disabled={isSubmitting}
            placeholder="Select date"
            invalid={!!errors.startDate}
            errorId="startDate-error"
          />
          {errors.startDate && (
            <p id="startDate-error" className="text-xs text-destructive" role="alert">
              {errors.startDate}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="probationEndDate" className="text-xs font-medium text-muted-foreground">
            Probation end date
          </Label>
          <DatePicker
            id="probationEndDate"
            value={probationEndDate}
            onChange={setProbationEndDate}
            disabled={isSubmitting}
            placeholder="Select date"
          />
        </div>
      </div>

      {canAssignRole && (
        <div className="space-y-1.5">
          <Label htmlFor="role" className="text-xs font-medium text-muted-foreground">
            System role
          </Label>
          <Select
            value={roleId || '__none__'}
            onValueChange={(value) => setRoleId(value === '__none__' ? '' : value)}
            disabled={isSubmitting}
          >
            <SelectTrigger id="role" className="h-9">
              <SelectValue placeholder="No account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No account</SelectItem>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id} disabled={lockedRoleIds.has(role.id)}>
                  {role.name}
                  {role.description ? ` — ${role.description}` : ''}
                  {lockedRoleIds.has(role.id) ? ' · Super Admin only' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            When set, a user account will be created so the employee can log in.
          </p>
          {lockedRoleIds.size > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Roles marked Super Admin only hold a permission only a Super Admin can grant, so only
              a Super Admin can give them to an account.
            </p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">
          Manager{requireManager && <span className="text-destructive"> *</span>}
        </Label>
        <ManagerCombobox
          selectedManagers={selectedManagers}
          onAddManager={addManager}
          onRemoveManager={removeManager}
          disabled={isSubmitting}
          invalid={!!errors.manager}
          errorId="manager-error"
        />
        {errors.manager && (
          <p id="manager-error" className="text-xs text-destructive" role="alert">
            {errors.manager}
          </p>
        )}
      </div>

      {extraSection}

      <DialogFooter className="gap-2 sm:gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
          className="cursor-pointer"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} className="cursor-pointer">
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}
